import { html, type TemplateResult } from 'lit'
// Module scope, so there is no `this.classMap` to reach for. Every key below is
// a single token, which is the whole hazard that wrapper exists to absorb — it
// splits multi-token keys before `classList.add()` sees them — so the raw
// directive is safe here by construction.
import { classMap } from 'lit/directives/class-map.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { when } from 'lit/directives/when.js'
import '../icons/icon'
import type { SortDirection, TableColumn, TableDensity } from './table'

/** The two settled directions. Unsorted renders `unfold_more`, which advertises
 *  that the column sorts without claiming a direction it does not have. */
const GLYPH = { asc: 'arrow_upward', desc: 'arrow_downward' } as const

/** The deprecated physical spellings, and what each one means logically. */
const LEGACY_ALIGN = { left: 'start', right: 'end' } as const

let warnedLegacyAlign = false

/**
 * The column's alignment as one of the three logical values, whichever
 * vocabulary it was declared in. Resolved here rather than at each use, so the
 * cell box and the sort indicator's geometry can never read it differently.
 */
function alignOf<T extends Record<string, any>>(column: TableColumn<T>): 'start' | 'end' | 'center' {
	const declared = column.align
	if (declared !== 'left' && declared !== 'right') return declared ?? 'start'
	if (!warnedLegacyAlign) {
		warnedLegacyAlign = true
		console.warn(
			`[schmancy-table] column align="${declared}" is deprecated — use the logical "${LEGACY_ALIGN[declared]}", which stays correct under RTL.`,
		)
	}
	return LEGACY_ALIGN[declared]
}

/**
 * The single implementation of a column's box: alignment, density floor, and
 * the min-width that lets a `minmax(0,1fr)` track resolve. Header and body both
 * render through it, so a header whose ink lands somewhere its own column's
 * body ink does not is not a state this component can reach.
 *
 * `grid` + `content-center`, never `flex`: a flex cell shrink-wraps its child,
 * and `text-align` then positions nothing — that is exactly how the header's
 * `align` became a no-op while the body's worked. A grid cell's single
 * anonymous item stretches the full track instead, so `text-align` inside it is
 * the one mechanism both roles share. Which binds every caller to hand this
 * cell exactly ONE in-flow child: a second one is a second grid row, not a
 * second word on the line.
 */
function cellClasses<T extends Record<string, any>>(
	column: TableColumn<T>,
	density: TableDensity,
): Record<string, boolean> {
	const align = alignOf(column)
	return {
		grid: true,
		'content-center': true,
		// Without it a grid item's automatic minimum size is its content, the
		// track never falls below that, and a consumer's own `truncate` never
		// has a narrower box to clip against.
		'min-w-0': true,
		'py-2': true,
		// A floor, not a height: a two-line cell grows past it and takes the
		// whole row with it. The header reads the same rung as the body, so the
		// two bands cannot disagree on height either.
		'min-h-control-sm': density === 'default',
		'min-h-control-lg': density === 'comfortable',
		'text-start': align === 'start',
		'text-end': align === 'end',
		'text-center': align === 'center',
		// Figures a reader scans down a column line up only on a tabular face.
		'tabular-nums': align === 'end',
		// Containing block for the centred column's out-of-flow indicator.
		relative: align === 'center',
	}
}

/**
 * A header cell, and a `<button>` exactly when the column opts into sorting and
 * renders no header of its own. A column that supplies `headerRender` owns that
 * cell's content and its clicks: wrapping it would nest the consumer's own
 * control inside a button, where every click on it also toggles the sort and
 * assistive technology has no defined reading of the nested control. Such a
 * column still sorts through the controlled `sort` property, and the affordance
 * is the consumer's to place inside their own header.
 *
 * `direction` is this column's own sort direction, or `null` when some other
 * column carries the sort. It reaches `aria-sort` whichever branch renders, so a
 * column sorted from outside still announces which way it runs.
 */
export function tableHeaderCell<T extends Record<string, any> = any>(
	column: TableColumn<T>,
	density: TableDensity,
	direction: SortDirection,
	onSort: (column: TableColumn<T>) => void,
): TemplateResult {
	const align = alignOf(column)
	const label = when(
		column.headerRender,
		() => column.headerRender?.(),
		() => html`${column.name}`,
	)
	const classes = {
		...cellClasses(column, density),
		'text-label-md': true,
		'text-surface-on-variant': true,
	}
	const sorted = direction === 'asc' ? 'ascending' : direction === 'desc' ? 'descending' : undefined

	if (column.sortable !== true || column.headerRender) {
		return html`<div role="columnheader" aria-sort=${ifDefined(sorted)} class=${classMap(classes)}>${label}</div>`
	}

	// Present whatever the sort state, so toggling a direction never re-flows the
	// label out from under the pointer that just clicked it. For a centred column
	// it goes out of flow instead: an inline glyph inside a centred line box
	// displaces the label by half its own width, which measured 10px of drift
	// against the same column's body cells in both engines.
	const indicator = html`<schmancy-icon
		class=${classMap({
			'text-base': true,
			'align-middle': true,
			'transition-opacity': true,
			'opacity-100': direction !== null,
			'opacity-0': direction === null,
			'group-hover:opacity-40': direction === null,
			'ms-1': align === 'start',
			'me-1': align === 'end',
			absolute: align === 'center',
			'end-0': align === 'center',
			'top-1/2': align === 'center',
			'-translate-y-1/2': align === 'center',
		})}
		>${direction ? GLYPH[direction] : 'unfold_more'}</schmancy-icon
	>`

	// The indicator and the label ride ONE inline run, so the cell keeps the
	// single in-flow child every body cell has and `text-align` — the one
	// mechanism both roles read — positions the pair together. Left as two
	// children of a single-track grid they are two grid items on two rows:
	// the indicator stacked under its own label, and a header band 8px past
	// the rung its body rows sit on.
	return html`<button
		type="button"
		role="columnheader"
		aria-sort=${ifDefined(sorted)}
		class=${classMap({ ...classes, group: true, 'cursor-pointer': true, 'select-none': true })}
		@click=${() => onSort(column)}
	><span class="min-w-0">${when(align === 'end', () => indicator)}${label}${when(align !== 'end', () => indicator)}</span></button>`
}

/**
 * A body cell. A `render()` returning a `TemplateResult` is emitted into the
 * cell with no element of its own around it — the cell already carries the body
 * ramp and the alignment, and a typography wrapper here line-clamped whatever
 * the consumer returned and nested typography inside their own.
 */
export function tableBodyCell<T extends Record<string, any> = any>(
	column: TableColumn<T>,
	item: T,
	density: TableDensity,
): TemplateResult {
	const classes = { ...cellClasses(column, density), 'text-body-md': true }
	return html`<div role="cell" class=${classMap(classes)}>${when(
		column.render,
		() => column.render?.(item),
		() => html`${column.key === undefined ? '' : String(item[column.key] ?? '')}`,
	)}</div>`
}
